/* Cookie consent banner. Works with Google Consent Mode v2 set up in index.html:
   analytics stays cookieless ("denied") until the visitor accepts here. The
   choice is remembered in localStorage; the footer's "Cookie settings" link
   reopens the banner via the `fw-cookie-settings` window event. */
import { useEffect, useState } from 'react';
import { Btn } from './ui';
import { useGo, navHandler, VIEW_PATHS } from './nav';

const KEY = 'fw_cookie_consent';
export const COOKIE_SETTINGS_EVENT = 'fw-cookie-settings';

type Choice = 'granted' | 'denied';

const readChoice = (): Choice | null => {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'granted' || v === 'denied' ? v : null;
  } catch {
    return null;
  }
};

const applyChoice = (choice: Choice) => {
  try {
    localStorage.setItem(KEY, choice);
  } catch {
    /* storage blocked — the choice still applies for this visit */
  }
  const w = window as unknown as { gtag?: (...args: unknown[]) => void };
  w.gtag?.('consent', 'update', { analytics_storage: choice });
};

export const CookieBanner = () => {
  const go = useGo();
  const [open, setOpen] = useState(() => readChoice() === null);
  useEffect(() => {
    const reopen = () => setOpen(true);
    window.addEventListener(COOKIE_SETTINGS_EVENT, reopen);
    return () => window.removeEventListener(COOKIE_SETTINGS_EVENT, reopen);
  }, []);
  if (!open) return null;
  const choose = (c: Choice) => {
    applyChoice(c);
    setOpen(false);
  };
  return (
    <div
      role='dialog'
      aria-live='polite'
      aria-label='Cookie preferences'
      style={{
        position: 'fixed',
        left: 20,
        bottom: 20,
        zIndex: 60,
        maxWidth: 420,
        width: 'calc(100% - 40px)',
        background: '#fff',
        border: '1px solid var(--line)',
        borderRadius: 18,
        boxShadow: 'var(--shadow-lg)',
        padding: '20px 22px',
      }}
    >
      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)' }}>Cookies</div>
      <p style={{ fontSize: 14, lineHeight: 1.55, color: 'var(--slate)', margin: '6px 0 0' }}>
        We use analytics cookies to understand how the site is used — only if you agree. See our{' '}
        <a
          href={VIEW_PATHS.privacy}
          onClick={navHandler(go, 'privacy')}
          style={{ color: 'var(--blue)', fontWeight: 600 }}
        >
          privacy policy
        </a>
        .
      </p>
      <div style={{ display: 'flex', gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
        <Btn small onClick={() => choose('granted')}>
          Accept
        </Btn>
        <Btn small kind='ghost' onClick={() => choose('denied')}>
          Decline
        </Btn>
      </div>
    </div>
  );
};
